import { ajvResolver } from '@hookform/resolvers/ajv'
import { Form } from 'components/form'
import { useAlert } from 'contexts/alert'
import { IPaymentGatewayDTO } from 'data/dtos/commercial/i-payment-gateway-dto'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useHistory, useParams } from 'react-router-dom'
import PaymentGatewaySchema, { IPaymentGatewayFormData } from 'schema/comercial/payment-gateway'
import api from 'services/api'
import { useStyles } from './styles'

interface IRouteParams {
  id: string
}

const PaymentGatewayForm: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [readonly, setReadonly] = useState(false)
  const { closeAlert, openAlert } = useAlert()
  const params = useParams<IRouteParams>()
  const firstInputElement = useRef(null)
  const classes = useStyles()
  const history = useHistory()

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    control,
    setFocus,
  } = useForm<IPaymentGatewayFormData>({
    resolver: ajvResolver(PaymentGatewaySchema),
    defaultValues: {
      name: '',
      gatewayId: '',
      subscriptionId: '',
      apiKey: '',
      apiSecret: '',
      isInactive: false,
    },
  })

  const handleChange = (formField: any) => closeAlert()

  // main data load
  useEffect(() => {
    async function loadData() {
      const { id } = params

      // form data
      await api
        .get(`/payment-gateways/${id}`)
        .then((response) => {
          const { data } = response.data

          const paymentGatewayResult = {
            name: data.name,
            gatewayId: data.gatewayId,
            subscriptionId: data.subscriptionId,
            apiKey: data.apiKey,
            apiSecret: data.apiSecret,
            isInactive: data.isInactive,
          }

          return paymentGatewayResult
        })
        .then((paymentGatewayResult: IPaymentGatewayDTO) => {
          reset(paymentGatewayResult)
        })
        .catch((error) => {
          console.log(error)
          return error
        })
    }

    if (params.id) {
      loadData()
      setReadonly(true)
    }
  }, [params, params.id])

  // data save

  const onSubmit = useCallback(async (data: IPaymentGatewayDTO) => {
    setIsLoading(true)

    const payLoad: IPaymentGatewayDTO = {
      name: data.name,
      gatewayId: data.gatewayId,
      subscriptionId: data.subscriptionId,
      apiKey: data.apiKey,
      apiSecret: data.apiSecret,
      isInactive: data.isInactive,
    }

    if (params.id) {
      const { id } = params

      payLoad.id = id

      await api
        .put(`/payment-gateways`, payLoad)
        .then(() => {
          openAlert('Cliente atualizado com sucesso!', 'success')
          history.push('/payment-gateways')
        })
        .catch((error) => {
          openAlert(error?.response?.data?.data?.message ?? 'Ops! Algo inesperado aconteceu!', 'error')
        })
        .finally(() => setIsLoading(false))
    } else {
      await api
        .post('/payment-gateways', payLoad)
        .then(() => {
          openAlert('Dado cadastrado com sucesso!', 'success')
          reset()
        })
        .catch((error) => {
          openAlert(error?.response?.data?.data?.message ?? 'Ops! Algo inesperado aconteceu!', 'error')
        })
        .finally(() => setIsLoading(false))
    }
  }, [])

  return (
    <Form.Root onHandleSubmit={handleSubmit(onSubmit)}>
      <Form.Info
        title='Gateways de Pagamento'
        subTitle='Dados coletados sobre gateways de pagamento.'
      />
      <Form.Body>
        <Form.Input
          label='Nome'
          error={!!errors.name}
          errorMessage={errors?.name?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
          maxLength={100}
          name='name'
          rules={{ onChange: (e) => handleChange(e) }}
          register={register}
        />

        <Form.Input
          label='Id no Gateway'
          error={!!errors.gatewayId}
          errorMessage={errors?.gatewayId?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}
          maxLength={50}
          name='gatewayId'
          rules={{ onChange: (e) => handleChange(e) }}
          register={register}
        />

        <Form.Input
          label='Id da Subscrição'
          error={!!errors.subscriptionId}
          errorMessage={errors?.subscriptionId?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}
          maxLength={50}
          name='subscriptionId'
          rules={{ onChange: (e) => handleChange(e) }}
          register={register}
        />

        <Form.Input
          label='Chave da API'
          error={!!errors.apiKey}
          errorMessage={errors?.apiKey?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}
          maxLength={50}
          name='apiKey'
          rules={{ onChange: (e) => handleChange(e) }}
          register={register}
        />

        <Form.Input
          label='Senha da API'
          error={!!errors.apiSecret}
          errorMessage={errors?.apiSecret?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}
          maxLength={50}
          name='apiSecret'
          rules={{ onChange: (e) => handleChange(e) }}
          register={register}
        />

        <Form.CheckBox
          label='Inativo'
          sizing={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}
          name='isInactive'
          control={control}
        />
      </Form.Body>

      <Form.Divisor />

      <Form.Footer>
        <Form.Button
          label='SALVAR'
          type='submit'
          variant='contained'
          isLoading={isLoading}
        />
        <Form.Button
          label='CANCELAR'
          type='cancel'
          variant='text'
          isLoading={isLoading}
        />
      </Form.Footer>
    </Form.Root>
  )
}

export default PaymentGatewayForm
